import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { t, font, Panel, Button, Badge } from "@realtyflows/ui";
import {
  listDataRequests,
  requestAccountDeletion,
  cancelDeletionRequest,
  dataExportUrl,
} from "../api";

/**
 * DataRights — V5 Feature 5.11 UI
 *
 * Self-service CCPA/CPRA data subject rights:
 *   - Download a full JSON export of every record we hold about you
 *   - Request account deletion (30-day grace; cancellable)
 *   - View past requests
 */
export default function DataRights({ user }) {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showDeleteForm, setShowDeleteForm] = useState(false);
  const [reason, setReason] = useState("");
  const [toast, setToast] = useState("");

  const refresh = () => {
    setLoading(true);
    listDataRequests()
      .then(setRequests)
      .catch(() => setRequests([]))
      .finally(() => setLoading(false));
  };
  useEffect(refresh, []);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 3000); };

  const handleRequestDeletion = async () => {
    setBusy(true);
    try {
      await requestAccountDeletion(reason);
      setReason(""); setShowDeleteForm(false);
      showToast("Deletion scheduled — you have 30 days to cancel");
      refresh();
    } catch (err) {
      window.alert(err.message || "Request failed");
    } finally {
      setBusy(false);
    }
  };

  const handleCancelDeletion = async (id) => {
    if (!window.confirm("Cancel this deletion request?")) return;
    try {
      await cancelDeletionRequest(id);
      showToast("Deletion cancelled");
      refresh();
    } catch (err) {
      window.alert(err.message || "Cancel failed");
    }
  };

  const pendingDeletion = requests.find((r) => r.kind === "deletion" && r.status === "pending");

  return (
    <div style={{ animation: "fadeSlide 0.3s ease", maxWidth: 700, margin: "0 auto" }}>
      <div style={{ marginBottom: 16 }}>
        <button onClick={() => navigate("/settings")} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, color: t.textMuted, fontFamily: font.body }}>
          ← Back to Settings
        </button>
      </div>

      <Panel padding={24} style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 20, fontWeight: 800, color: t.textPrimary, fontFamily: font.display }}>
          Data Rights & Privacy
        </div>
        <div style={{ fontSize: 13, color: t.textMuted, marginTop: 4, fontFamily: font.body, lineHeight: 1.6 }}>
          You have the right to access and delete your data under CCPA/CPRA and similar regulations. Use this page to download everything we hold about you, or to request account deletion.
        </div>
      </Panel>

      {/* Pending deletion banner */}
      {pendingDeletion && (
        <Panel padding={20} style={{ marginBottom: 16, background: t.coral + "08", border: `2px solid ${t.coral}40` }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 800, color: t.coral, fontFamily: font.display }}>
                ⚠ Account deletion scheduled
              </div>
              <div style={{ fontSize: 12, color: t.textPrimary, marginTop: 4, fontFamily: font.body, lineHeight: 1.5 }}>
                Your account will be permanently deleted on <strong>{new Date(pendingDeletion.scheduled_for).toLocaleDateString()}</strong>. You can cancel any time before then.
              </div>
            </div>
            <Button variant="primary" size="sm" onClick={() => handleCancelDeletion(pendingDeletion.id)}>
              Cancel deletion
            </Button>
          </div>
        </Panel>
      )}

      {/* Export */}
      <Panel padding={20} style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, marginBottom: 6, fontFamily: font.display }}>
          Download your data
        </div>
        <div style={{ fontSize: 12, color: t.textMuted, marginBottom: 14, fontFamily: font.body, lineHeight: 1.5 }}>
          Includes your profile, every client/deal/folder you've ever touched, all activities, all integrations, AI usage history, billing events — everything. Machine-readable JSON for easy import elsewhere.
        </div>
        <a
          href={`${dataExportUrl()}?token=${localStorage.getItem("realtyflows_token") || ""}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: "none" }}
        >
          <Button variant="primary">Download data export (JSON)</Button>
        </a>
      </Panel>

      {/* Deletion */}
      {!pendingDeletion && (
        <Panel padding={20} style={{ marginBottom: 16, borderColor: t.coral + "30" }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: t.coral, marginBottom: 6, fontFamily: font.display }}>
            Delete your account
          </div>
          <div style={{ fontSize: 12, color: t.textMuted, marginBottom: 14, fontFamily: font.body, lineHeight: 1.5 }}>
            Requests a full deletion of your account and all linked records. We hold the request for 30 days so you can cancel if you change your mind. After that, all your data is permanently destroyed — including clients, deals, folders, and uploaded documents.
          </div>

          {!showDeleteForm ? (
            <Button variant="outline" onClick={() => setShowDeleteForm(true)} style={{ color: t.coral, borderColor: t.coral }}>
              Request account deletion
            </Button>
          ) : (
            <div>
              <div style={{ fontSize: 11, color: t.textSecondary, fontWeight: 600, marginBottom: 5, fontFamily: font.body }}>
                Optional reason (helps us improve)
              </div>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Why are you leaving?"
                rows={3}
                style={{ width: "100%", padding: 12, fontSize: 13, fontFamily: font.body, border: `1.5px solid ${t.border}`, borderRadius: 6, background: t.surfaceWarm, color: t.textPrimary, outline: "none", resize: "vertical", marginBottom: 12 }}
              />
              <div style={{ display: "flex", gap: 8 }}>
                <Button variant="primary" onClick={handleRequestDeletion} disabled={busy} style={{ background: t.coral }}>
                  {busy ? "Submitting..." : "Confirm deletion request"}
                </Button>
                <Button variant="outline" onClick={() => { setShowDeleteForm(false); setReason(""); }}>Cancel</Button>
              </div>
            </div>
          )}
        </Panel>
      )}

      {/* Request history */}
      <Panel padding={20}>
        <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, marginBottom: 14, fontFamily: font.display }}>
          Request History
        </div>
        {loading ? (
          <div style={{ fontSize: 12, color: t.textMuted, fontFamily: font.body }}>Loading...</div>
        ) : requests.length === 0 ? (
          <div style={{ fontSize: 12, color: t.textMuted, fontStyle: "italic", fontFamily: font.body }}>
            No data rights requests yet.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {requests.map((r) => (
              <div key={r.id} style={{
                display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10,
                padding: 10, background: t.surfaceWarm,
                border: `1px solid ${t.border}`, borderRadius: 6,
              }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: t.textPrimary, fontFamily: font.body }}>
                    {r.kind === "export" ? "Data Export" : "Account Deletion"}
                  </div>
                  <div style={{ fontSize: 10, color: t.textMuted, fontFamily: font.mono }}>
                    {new Date(r.created_at).toLocaleString()}
                    {r.scheduled_for && r.status === "pending" && ` · scheduled for ${new Date(r.scheduled_for).toLocaleDateString()}`}
                  </div>
                </div>
                <Badge color={
                  r.status === "completed" ? t.emerald
                  : r.status === "pending" ? t.amber
                  : r.status === "cancelled" ? t.textMuted
                  : t.sky
                }>
                  {r.status}
                </Badge>
              </div>
            ))}
          </div>
        )}
      </Panel>

      {toast && (
        <div style={{
          position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
          background: t.emerald, color: "#fff", padding: "10px 18px",
          borderRadius: 8, fontSize: 13, fontWeight: 600, fontFamily: font.body,
          boxShadow: "0 4px 16px rgba(0,0,0,0.15)", zIndex: 9999,
        }}>{toast}</div>
      )}
    </div>
  );
}
